import React, { useCallback, useEffect, useRef, useState } from "react";
import {
  AlertDialog,
  AlertDialogBody,
  AlertDialogContent,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogOverlay,
  Box,
  Button,
  Td,
  Text,
  Tr,
  useDisclosure,
  useToast,
} from "@chakra-ui/react";
import { AddIcon, DeleteIcon } from "@chakra-ui/icons";
import { FaBuilding } from "react-icons/fa";
import { useTranslation } from "react-i18next";
import { listPartnerLinks, deletePartnerLink } from "../../../api/partnersApi";
import { useBusinessUnit } from "../../../context/BusinessUnitContext";
import {
  PARTNERS_PARTNER_LINK_STATUS_LABELS,
  PARTNERS_PARTNER_LINK_STATUS_LABEL_KEYS,
  PARTNERS_PARTNER_LINK_STATUS_COLORS,
  getStatusLabel,
} from "../../../constants/statuses";
import {
  Panel,
  DataTable,
  StatusBadge,
  MiniIconButton,
  EmptyState,
  TablePagination,
} from "../../../components/Portal/ui";
import PartnerLinkFormModal from "./PartnerLinkFormModal";

const AUTO_REFRESH_INTERVAL = 30000;

const PartnersCompanyList = ({ onTotalChange }) => {
  const toast = useToast();
  const { t } = useTranslation("portal");
  const { selectedBusinessUnitId, isLoaded: isBUContextLoaded } = useBusinessUnit();

  const [items, setItems] = useState([]);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [totalItems, setTotalItems] = useState(0);
  const [totalPages, setTotalPages] = useState(1);
  const [loading, setLoading] = useState(false);

  const createModal = useDisclosure();
  const deleteDialog = useDisclosure();
  const [deletingLinkId, setDeletingLinkId] = useState(null);
  const [deleteLoading, setDeleteLoading] = useState(false);
  const cancelDeleteRef = useRef(null);
  const refreshTimerRef = useRef(null);

  const fetchLinks = useCallback(async (silent = false) => {
    if (!silent) setLoading(true);
    try {
      const res = await listPartnerLinks(
        { page, pageSize },
        { buId: selectedBusinessUnitId || undefined }
      );
      setItems(res.items || []);
      setTotalItems(res.totalItems || 0);
      setTotalPages(res.totalPages || Math.max(1, Math.ceil((res.totalItems || 0) / pageSize)));
    } catch (e) {
      if (!silent) {
        toast({ title: t("partners.partnerLinks.fetchError", "Failed to load partner links"), status: "error", duration: 3000 });
      }
    } finally {
      if (!silent) setLoading(false);
    }
  }, [page, pageSize, selectedBusinessUnitId, toast, t]);

  useEffect(() => {
    if (isBUContextLoaded) fetchLinks();
  }, [fetchLinks, isBUContextLoaded]);

  // Surface the total to the parent KPI strip.
  useEffect(() => {
    if (onTotalChange) onTotalChange(totalItems);
  }, [totalItems, onTotalChange]);

  useEffect(() => {
    if (!isBUContextLoaded) return;
    refreshTimerRef.current = setInterval(() => fetchLinks(true), AUTO_REFRESH_INTERVAL);
    return () => clearInterval(refreshTimerRef.current);
  }, [fetchLinks, isBUContextLoaded]);

  const openDeleteDialog = (linkId) => {
    setDeletingLinkId(linkId);
    deleteDialog.onOpen();
  };

  const handleDeleteConfirm = async () => {
    setDeleteLoading(true);
    try {
      await deletePartnerLink(deletingLinkId, { buId: selectedBusinessUnitId || undefined });
      toast({ title: t("partners.partnerLinks.removed", "Partner link removed"), status: "success", duration: 3000 });
      fetchLinks();
    } catch (e) {
      toast({ title: t("partners.partnerLinks.removeError", "Failed to remove partner link"), status: "error", duration: 3000 });
    } finally {
      setDeleteLoading(false);
      setDeletingLinkId(null);
      deleteDialog.onClose();
    }
  };

  const columns = [
    { key: "company", label: t("partners.partnerLinks.companyName", "Company"), width: "30%" },
    { key: "code", label: t("partners.partnerLinks.code", "Code"), width: "24%" },
    { key: "status", label: t("common.status", "Status"), width: "16%" },
    { key: "createdAt", label: t("common.createdAt", "Created"), width: "16%" },
    { key: "actions", label: "", width: "14%", align: "right" },
  ];

  return (
    <Panel
      h="100%"
      w="100%"
      title={t("partners.partnerLinks.title", "Partner Companies")}
      count={
        loading
          ? t("partners.partnerLinks.summary.loading", "Loading...")
          : t("partners.partnerLinks.summary.total", { count: totalItems, defaultValue: "{{count}} partner(s)" })
      }
      actions={
        <Button
          variant="primary"
          size="sm"
          h="28px"
          px="10px"
          fontSize="11px"
          leftIcon={<AddIcon boxSize="9px" />}
          onClick={createModal.onOpen}
        >
          {t("partners.partnerLinks.add", "Add Partner")}
        </Button>
      }
      footer={
        <TablePagination
          page={page}
          pageSize={pageSize}
          totalItems={totalItems}
          totalPages={totalPages}
          onPageChange={setPage}
          onPageSizeChange={(sz) => { setPageSize(sz); setPage(1); }}
        />
      }
    >
      <DataTable
        columns={columns}
        rows={items}
        loading={loading}
        emptyState={
          <EmptyState
            icon={<FaBuilding />}
            title={t("partners.partnerLinks.emptyTitle", "No partner companies yet")}
            hint={t("partners.partnerLinks.empty", "No partner companies yet. Add one by entering their partner code.")}
          />
        }
        renderRow={(link) => (
          <Tr key={link.id}>
            <Td fontWeight="700">
              {link.partnerCompanyName || `Company #${link.partnerCompanyId}`}
              {link.partnerBuName && (
                <Text fontSize="11px" fontWeight="600" color="portal.muted" sx={{ marginBottom: "0 !important" }}>
                  {link.partnerBuName}
                </Text>
              )}
            </Td>
            <Td>
              <Box
                as="span"
                fontFamily="ui-monospace, SFMono-Regular, Menlo, monospace"
                color="portal.primary"
                fontWeight="800"
              >
                {link.partnerCode}
              </Box>
            </Td>
            <Td>
              <StatusBadge
                label={getStatusLabel(
                  t,
                  PARTNERS_PARTNER_LINK_STATUS_LABEL_KEYS,
                  PARTNERS_PARTNER_LINK_STATUS_LABELS,
                  link.status
                )}
                colorScheme={PARTNERS_PARTNER_LINK_STATUS_COLORS[link.status] || "gray"}
              />
            </Td>
            <Td color="portal.muted">
              {link.createdAt ? new Date(link.createdAt).toLocaleDateString() : "-"}
            </Td>
            <Td>
              <Box display="flex" justifyContent="flex-end">
                <MiniIconButton
                  aria-label={t("partners.partnerLinks.remove", "Remove")}
                  icon={<DeleteIcon />}
                  color="portal.danger"
                  _hover={{ bg: "danger.50", borderColor: "portal.danger", color: "portal.danger" }}
                  onClick={() => openDeleteDialog(link.id)}
                />
              </Box>
            </Td>
          </Tr>
        )}
      />

      <PartnerLinkFormModal
        isOpen={createModal.isOpen}
        onClose={createModal.onClose}
        onSuccess={fetchLinks}
      />

      <AlertDialog
        isOpen={deleteDialog.isOpen}
        leastDestructiveRef={cancelDeleteRef}
        onClose={deleteDialog.onClose}
      >
        <AlertDialogOverlay>
          <AlertDialogContent>
            <AlertDialogHeader fontSize="lg" fontWeight="bold">
              {t("partners.partnerLinks.removeTitle", "Remove Partner")}
            </AlertDialogHeader>
            <AlertDialogBody>
              {t("partners.partnerLinks.confirmRemove", "Are you sure you want to remove this partner link? You can always add it back later.")}
            </AlertDialogBody>
            <AlertDialogFooter>
              <Button ref={cancelDeleteRef} onClick={deleteDialog.onClose}>
                {t("common.cancel", "Cancel")}
              </Button>
              <Button colorScheme="red" onClick={handleDeleteConfirm} isLoading={deleteLoading} ml={3}>
                {t("partners.partnerLinks.removeAction", "Remove")}
              </Button>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialogOverlay>
      </AlertDialog>
    </Panel>
  );
};

export default PartnersCompanyList;
